/**
 * 商家端 - 发票（服务费票为主）
 *
 * 商家作为服务方，向工厂开具 6% 服务费票；商品票由工厂开具（平台代开）。
 * 后端 /invoices 对 SELLER 返回 issuer_id=自己 的票（即服务费票）。
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, Table, Badge, useApi, money, Loading, Button } from '../../components/ui'
import { HelpBubble } from '../../components/HelpBubble'
import { api } from '../../api/client'

interface Invoice {
  invoice_id: string
  order_id: string
  invoice_category: string
  invoice_type: string
  issuer_id: string
  issuer_role: string
  amount: string
  tax_rate: string
  remark: string
  item_name: string
  invoice_no: string | null
  pdf_url: string | null
  status: string
  issued_at: string | null
}

function categoryLabel(c: string): string {
  if (c === 'GOODS') return '商品票'
  if (c === 'SERVICE_FEE') return '服务费票'
  return c
}

function taxLabel(rate: string): string {
  const n = Number(rate)
  return `${(n * 100).toFixed(0)}%`
}

export function SellerInvoices() {
  const nav = useNavigate()
  const list = useApi<Invoice[]>(async () => api.get('/invoices'), [])
  const [preview, setPreview] = useState<Invoice | null>(null)

  const all = list.data ?? []
  const issued = all.filter((i) => i.status === 'ISSUED')
  const needRedFlush = all.filter((i) => i.status === 'RED_FLUSHING' || i.status === 'RED_FLUSHED')
  const issuing = all.filter((i) => i.status === 'ISSUING' || i.status === 'FAILED')

  const issuedAmount = issued.reduce((sum, i) => sum + Number(i.amount), 0)

  const columns = [
    { key: 'invoice_no', title: '发票号', render: (r: Invoice) => r.invoice_no ? <a onClick={() => setPreview(r)} style={{ color: '#2563eb', cursor: 'pointer' }}>{r.invoice_no}</a> : '-' },
    { key: 'order_id', title: '订单号', render: (r: Invoice) => <a onClick={() => nav(`/seller/orders?detail=${r.order_id}`)} style={{ color: '#2563eb', cursor: 'pointer' }}>{r.order_id}</a> },
    { key: 'invoice_category', title: '类别', render: (r: Invoice) => categoryLabel(r.invoice_category) },
    { key: 'amount', title: '金额', render: (r: Invoice) => money(r.amount) },
    { key: 'tax_rate', title: '税率', render: (r: Invoice) => taxLabel(r.tax_rate) },
    { key: 'status', title: '状态', render: (r: Invoice) => <Badge status={r.status} /> },
    { key: 'remark', title: '备注' },
  ]

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>发票（服务费票）</h2>

      <div style={{ background: '#fef9c3', border: '1px solid #fde68a', color: '#854d0e', padding: '10px 14px', borderRadius: 6, marginBottom: 16, fontSize: 13 }}>
        商家作为服务方，向工厂开具 6% 服务费票。点订单号看订单详情，点发票号预览数电票。
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16, marginBottom: 16 }}>
        <MetricCard label="已开票金额" value={money(issuedAmount)} />
        <MetricCard label="需要红冲" value={String(needRedFlush.length)} />
        <MetricCard label="开票中/失败" value={String(issuing.length)} />
      </div>

      {list.loading ? (
        <Loading />
      ) : (
        <>
          <Card title={`已开票（${issued.length}）`}>
            <Table<Invoice> rows={issued} empty="无已开票" columns={columns} />
          </Card>

          {needRedFlush.length > 0 && (
            <Card title={`需要红冲（${needRedFlush.length}）`}>
              <Table<Invoice> rows={needRedFlush} empty="无" columns={columns} />
            </Card>
          )}

          {issuing.length > 0 && (
            <Card title={`开票中/失败（${issuing.length}）`}>
              <Table<Invoice> rows={issuing} empty="无" columns={columns} />
            </Card>
          )}
        </>
      )}

      {/* 发票预览弹层 */}
      {preview && (
        <div onClick={() => setPreview(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 24 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 8, maxWidth: 440, maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #e5e7eb' }}>
              <strong>{categoryLabel(preview.invoice_category)}预览</strong>
              <Button size="sm" variant="default" onClick={() => setPreview(null)}>关闭</Button>
            </div>
            {preview.pdf_url && preview.status === 'ISSUED' ? (
              <img src={preview.pdf_url} alt={preview.invoice_no ?? ''} style={{ width: '100%', display: 'block' }} />
            ) : (
              <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888' }}>无预览（{preview.status}）</div>
            )}
            <div style={{ padding: 12, fontSize: 12, color: '#666' }}>
              <div>发票号：{preview.invoice_no ?? '-'}</div>
              <div>订单：<a onClick={() => nav(`/seller/orders?detail=${preview.order_id}`)} style={{ color: '#2563eb', cursor: 'pointer' }}>{preview.order_id}</a></div>
              <div>金额：{money(preview.amount)} · 税率：{taxLabel(preview.tax_rate)}</div>
              <div>备注：{preview.remark}</div>
            </div>
          </div>
        </div>
      )}

      <HelpBubble pageKey="seller-invoices" title="发票（服务费票）">
        <p>分账成功后平台自动代开双票：<b>商品票</b>（工厂→消费者，13%）和<b>服务费票</b>（商家→工厂，6%）。商家此处只看自己的服务费票。</p>
        <p>点发票号预览数电票图片，点订单号跳订单详情。退款时已开票的会自动红冲。</p>
      </HelpBubble>
    </div>
  )
}

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ background: '#fff', padding: 16, borderRadius: 8, border: '1px solid #e5e7eb' }}>
      <div style={{ color: '#888', fontSize: 12 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>{value}</div>
    </div>
  )
}
